import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  combinacionesDeVariantes,
  cruzarTallesYColores,
  separarPorComas,
} from '@actualgold/shared';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Interruptor, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { aplanarElArbol, etiquetaSangrada, type Rubro } from './Rubros';
import { CamposDeLaFicha } from '../componentes/CamposDeLaFicha';

interface Unidad {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly decimales: number;
}

interface VarianteDeLaLista {
  readonly id: string;
  readonly codigo: string;
  readonly talle: string | null;
  readonly color: string | null;
  readonly esUnica: boolean;
  readonly activa: boolean;
}

interface RubroDelArticulo {
  readonly id: string;
  readonly nombre: string;
}

export interface Articulo {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly marca: string | null;
  readonly tipo: 'producto' | 'servicio';
  readonly unidad: string;
  readonly activo: boolean;
  readonly rubros: readonly RubroDelArticulo[];
  readonly campos: Readonly<Record<string, string>>;
  readonly variantes: readonly VarianteDeLaLista[];
}

/**
 * Elegir rubros: una lista de casillas, no un selector.
 *
 * **Un artículo puede estar en varios** —es lo que pidió el dueño del producto—, y un selector
 * múltiple del navegador exige saber que hay que apretar Ctrl para agregar el segundo. Con casillas
 * no hay nada que saber.
 */
export function ElegirRubros({
  rubros,
  elegidos,
  onCambiar,
}: {
  readonly rubros: readonly Rubro[];
  readonly elegidos: readonly string[];
  readonly onCambiar: (elegidos: readonly string[]) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  // Los inactivos no se ofrecen, pero **sí se muestran si el artículo ya los tiene**: esconderlos
  // haría que guardar una corrección se los sacara sin que nadie lo pidiera.
  const visibles = aplanarElArbol(rubros).filter(
    (rubro) => rubro.activa || elegidos.includes(rubro.id),
  );

  if (visibles.length === 0) {
    return <p className="text-apoyo text-texto-tenue">{t('rubros.vacio')}</p>;
  }

  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-1 text-apoyo font-medium">{t('rubros.delArticulo')}</legend>
      <div className="max-h-40 overflow-y-auto rounded border border-borde-tenue p-2">
        {visibles.map((rubro) => (
          <label
            key={rubro.id}
            className="flex items-center gap-2 py-0.5 text-cuerpo"
            style={{ paddingLeft: `${String(rubro.profundidad * 1)}rem` }}
          >
            <input
              type="checkbox"
              checked={elegidos.includes(rubro.id)}
              onChange={(evento) => {
                onCambiar(
                  evento.target.checked
                    ? [...elegidos, rubro.id]
                    : elegidos.filter((id) => id !== rubro.id),
                );
              }}
            />
            {rubro.nombre}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

interface FilaDeCodigoDeBarra {
  readonly varianteId: string;
  readonly varianteCodigo: string;
  readonly talle: string | null;
  readonly color: string | null;
  readonly esUnica: boolean;
  readonly codigo: string | null;
}

const TRAZABILIDADES = ['ninguno', 'lote', 'lote_con_vencimiento', 'serie'] as const;

/** Cómo se nombra una unidad en el selector: "KG — Kilogramo". */
function etiquetaDeUnidad(unidad: Unidad): string {
  return `${unidad.codigo} — ${unidad.nombre}`;
}

/** `lote_con_vencimiento` → `trazabilidadLoteConVencimiento`. */
function claveDeTrazabilidad(valor: string): string {
  const camello = valor.replace(/_(.)/g, (_, letra: string) => letra.toUpperCase());
  return `catalogo.trazabilidad${camello.charAt(0).toUpperCase()}${camello.slice(1)}`;
}

/**
 * Artículos: la primera pantalla del catálogo.
 *
 * Dos decisiones que se ven al usarla. La primera: **las variantes no se cargan de a una**.
 * Quien vende remeras escribe los talles y los colores separados por coma y el sistema
 * arma el cruce; cargar doce combinaciones a mano es el trabajo que hace que nadie use el
 * módulo. La segunda: el aviso de cuántas variantes se van a crear cuenta con la **misma**
 * función que después usa el servidor, así que no puede prometer algo distinto de lo que
 * pasa al guardar.
 */
export function Articulos(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeCrear = usePermiso('CATALOGO.ARTICULO.CREAR');

  const [busqueda, setBusqueda] = useState('');
  const [rubroFiltrado, setRubroFiltrado] = useState('');
  const [rubrosElegidos, setRubrosElegidos] = useState<readonly string[]>([]);
  /** Los campos propios del comercio, en el alta. La corrección vive en la ficha. */
  const [camposDelAlta, setCamposDelAlta] = useState<Record<string, string>>({});
  const [abierto, setAbierto] = useState(false);
  const [errorDelAlta, setErrorDelAlta] = useState<string | null>(null);

  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [marca, setMarca] = useState('');
  const [tipo, setTipo] = useState<'producto' | 'servicio'>('producto');
  const [unidadDeStockId, setUnidadDeStockId] = useState('');
  const [controlaExistencia, setControlaExistencia] = useState(true);
  const [trazabilidad, setTrazabilidad] = useState<(typeof TRAZABILIDADES)[number]>('ninguno');
  const [esInsumo, setEsInsumo] = useState(false);
  const [talles, setTalles] = useState('');
  const [colores, setColores] = useState('');
  const [codigosDeBarra, setCodigosDeBarra] = useState('');

  const unidades = useQuery({
    queryKey: ['unidades-de-medida'],
    queryFn: () => pedir<Unidad[]>('/unidades-de-medida'),
  });

  const rubros = useQuery({
    queryKey: ['categorias'],
    queryFn: () => pedir<Rubro[]>('/categorias'),
  });

  const consulta = useQuery({
    queryKey: ['articulos', busqueda, rubroFiltrado],
    queryFn: () => {
      // El filtro por rubro lo resuelve el servidor y no la pantalla: filtrar acá filtraría los
      // doscientos que llegaron, no los que hay.
      const parametros = new URLSearchParams();
      if (busqueda.trim() !== '') parametros.set('texto', busqueda.trim());
      if (rubroFiltrado !== '') parametros.set('rubroId', rubroFiltrado);
      const consultaEnLaRuta = parametros.toString();
      return pedir<Articulo[]>(
        consultaEnLaRuta === '' ? '/articulos' : `/articulos?${consultaEnLaRuta}`,
      );
    },
  });

  // Con la misma función que usa el servidor: el aviso no puede desviarse del resultado.
  const aCrear = useMemo(
    () =>
      combinacionesDeVariantes(
        codigo.trim() === '' ? '—' : codigo.trim(),
        cruzarTallesYColores(separarPorComas(talles), separarPorComas(colores)),
      ),
    [codigo, talles, colores],
  );

  function limpiarElFormulario(): void {
    setCodigo('');
    setNombre('');
    setMarca('');
    setTipo('producto');
    // Vacío significa "la de por defecto", así que el próximo alta arranca igual que ésta.
    setUnidadDeStockId('');
    setControlaExistencia(true);
    setTrazabilidad('ninguno');
    setEsInsumo(false);
    setTalles('');
    setColores('');
    setCodigosDeBarra('');
    setRubrosElegidos([]);
    setCamposDelAlta({});
    setErrorDelAlta(null);
  }

  /**
   * El artículo que se está corrigiendo, con lo que se está escribiendo adentro.
   *
   * Se guarda el original además de lo editado para poder mandar **sólo lo que cambió**: la ruta
   * entiende que un campo ausente es «no lo toques», y aprovecharlo hace que dos personas
   * corrigiendo cosas distintas del mismo artículo no se pisen.
   */
  /**
   * El artículo cuyos códigos de barra se están mirando.
   *
   * Es una pantalla aparte del alta porque **el código pertenece a la variante, no al artículo**:
   * un buzo talle M y uno talle L tienen etiquetas distintas, y el alta —que crea las variantes—
   * todavía no sabe cuáles son.
   */
  const [codigosDe, setCodigosDe] = useState<Articulo | null>(null);
  const [codigoNuevo, setCodigoNuevo] = useState('');
  const [varianteElegida, setVarianteElegida] = useState('');
  const [errorDeCodigos, setErrorDeCodigos] = useState<string | null>(null);

  const codigos = useQuery({
    queryKey: ['codigos-de-barra', codigosDe?.id],
    enabled: codigosDe !== null,
    queryFn: () =>
      pedir<FilaDeCodigoDeBarra[]>(`/articulos/${codigosDe?.id ?? ''}/codigos-de-barra`),
  });

  /**
   * La variante a la que va el código, **derivada y no copiada al estado**.
   *
   * Vacío significa «la primera», así que el navegador dibuja la primera opción. Si el estado se
   * quedara vacío, la pantalla mostraría una variante y el botón mandaría otra cosa —o nada—. Es
   * exactamente el defecto que apareció en E17 con el tipo de comprobante del mostrador, y se
   * resuelve igual: lo que se ve es lo que se manda porque son el mismo valor.
   */
  const varianteEfectiva =
    varianteElegida === '' ? (codigos.data?.[0]?.varianteId ?? '') : varianteElegida;

  const agregarCodigo = useMutation({
    mutationFn: () =>
      pedir<void>(`/variantes/${varianteEfectiva}/codigos-de-barra`, {
        method: 'POST',
        cuerpo: { codigo: codigoNuevo.trim() },
      }),
    onSuccess: async () => {
      setCodigoNuevo('');
      setErrorDeCodigos(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['codigos-de-barra'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeCodigos(textoDeError(fallo));
    },
  });

  const sacarCodigo = useMutation({
    mutationFn: (fila: FilaDeCodigoDeBarra) =>
      pedir<void>(`/variantes/${fila.varianteId}/codigos-de-barra`, {
        method: 'DELETE',
        cuerpo: { codigo: fila.codigo ?? '' },
      }),
    onSuccess: async () => {
      setErrorDeCodigos(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['codigos-de-barra'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeCodigos(textoDeError(fallo));
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: (fila: Articulo) =>
      pedir<{ id: string }>(`/articulos/${fila.id}`, {
        method: 'PATCH',
        cuerpo: { activo: !fila.activo },
      }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['articulos'] });
    },
  });

  const crear = useMutation({
    mutationFn: () => {
      const barras = separarPorComas(codigosDeBarra);
      return pedir<{ id: string; variantes: number }>('/articulos', {
        method: 'POST',
        cuerpo: {
          codigo: codigo.trim(),
          nombre: nombre.trim(),
          marca: marca.trim() === '' ? undefined : marca.trim(),
          tipo,
          unidadDeStockId: unidadEfectivaId,
          controlaExistencia: tipo === 'servicio' ? false : controlaExistencia,
          trazabilidad,
          esInsumo,
          combinaciones: cruzarTallesYColores(separarPorComas(talles), separarPorComas(colores)),
          codigosDeBarra: barras.length === 0 ? undefined : barras,
          rubros: rubrosElegidos.length === 0 ? undefined : rubrosElegidos,
          ...(Object.keys(camposDelAlta).length === 0 ? {} : { campos: camposDelAlta }),
        },
      });
    },
    onSuccess: async () => {
      setAbierto(false);
      limpiarElFormulario();
      await clienteDeConsultas.invalidateQueries({ queryKey: ['articulos'] });
    },
    onError: (fallo: unknown) => {
      setErrorDelAlta(textoDeError(fallo));
    },
  });

  const columnas: Columna<Articulo>[] = [
    {
      clave: 'codigo',
      titulo: t('catalogo.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    {
      clave: 'nombre',
      titulo: t('catalogo.nombre'),
      contenido: (fila) => (
        <>
          {fila.nombre}
          {fila.marca === null ? null : (
            <span className="block text-xs text-texto-tenue">{fila.marca}</span>
          )}
        </>
      ),
    },
    {
      clave: 'tipo',
      titulo: t('catalogo.tipo'),
      contenido: (fila) =>
        fila.tipo === 'servicio' ? t('catalogo.servicio') : t('catalogo.producto'),
    },
    { clave: 'unidad', titulo: t('catalogo.unidad'), contenido: (fila) => fila.unidad },
    {
      clave: 'rubros',
      titulo: t('rubros.titulo'),
      contenido: (fila) =>
        fila.rubros.length === 0 ? (
          <span className="text-texto-tenue">{t('rubros.ningunoElegido')}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {fila.rubros.map((rubro) => (
              <Insignia key={rubro.id} tono="neutro">
                {rubro.nombre}
              </Insignia>
            ))}
          </div>
        ),
    },
    {
      clave: 'variantes',
      titulo: t('catalogo.variantes'),
      contenido: (fila) => {
        // La variante única no se nombra: quien vende tornillos no tiene por qué aprender
        // esa palabra para usar el sistema.
        const reales = fila.variantes.filter((variante) => !variante.esUnica);
        if (reales.length === 0)
          return <span className="text-texto-tenue">{t('catalogo.sinDetalle')}</span>;
        return (
          <span title={reales.map((variante) => variante.codigo).join(', ')}>
            {t('catalogo.variantesContadas', { count: reales.length })}
          </span>
        );
      },
    },
    {
      clave: 'estado',
      titulo: t('catalogo.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activo ? 'bien' : 'neutro'}>
          {fila.activo ? t('catalogo.activo') : t('catalogo.inactivo')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      // **No hay borrar, y no es un olvido**: un artículo con ventas no se puede borrar sin dejar
      // comprobantes apuntando a la nada. Se desactiva, que es lo que de verdad se quiere decir.
      contenido: (fila) => (
        <div className="flex justify-end gap-1">
          {/* **Gestionar y no corregir**: lleva a la ficha, donde además de editar se ve qué le
              pasó al artículo. El diálogo de corrección se fue de acá: dos formularios de la misma
              cosa se separan a la primera corrección, y ya pasó dos veces. */}
          <Link to="/articulos/$id" params={{ id: fila.id }}>
            <Boton tono="principal">{t('ficha.gestionar')}</Boton>
          </Link>
          <Boton
            onClick={() => {
              setCodigosDe(fila);
              setCodigoNuevo('');
              setVarianteElegida('');
              setErrorDeCodigos(null);
            }}
          >
            {/* En la fila el rótulo es corto y en el diálogo es largo: la columna de acciones ya
                tiene tres botones, y «Códigos de barra» partido en dos renglones estira todas las
                filas de la tabla para explicar algo que el contexto ya dice. */}
            {t('catalogo.verCodigos')}
          </Boton>
          <Boton
            cargando={cambiarEstado.isPending}
            onClick={() => {
              cambiarEstado.mutate(fila);
            }}
          >
            {fila.activo ? t('catalogo.desactivar') : t('catalogo.activar')}
          </Boton>
        </div>
      ),
    },
  ];

  const listaDeUnidades = unidades.data ?? [];

  /**
   * La unidad efectiva se **deriva**, no se copia al estado cuando se abre el diálogo.
   *
   * Copiarla parece equivalente y no lo es: si la lista todavía no llegó, el estado queda
   * vacío y el navegador igual dibuja la primera opción. La pantalla muestra entonces una
   * unidad que el formulario no tiene, y el botón de guardar aparece bloqueado sin motivo
   * visible. Derivándola, lo que se ve es siempre lo que se manda.
   *
   * Y por defecto la unidad, no la primera alfabética: es con lo que se carga la mayoría de
   * los artículos, y quien vende al peso la cambia una vez.
   */
  const unidadPorDefecto =
    listaDeUnidades.find((unidad) => unidad.codigo === 'UN') ?? listaDeUnidades[0];
  const unidadEfectivaId = unidadDeStockId === '' ? (unidadPorDefecto?.id ?? '') : unidadDeStockId;
  const unidadElegida = listaDeUnidades.find((unidad) => unidad.id === unidadEfectivaId);

  return (
    <>
      <Encabezado
        titulo={t('catalogo.titulo')}
        descripcion={t('catalogo.descripcion')}
        acciones={
          puedeCrear ? (
            <Boton
              tono="principal"
              onClick={() => {
                // La unidad, no la primera alfabética: es con lo que se carga la mayoría
                // de los artículos, y el que vende al peso lo cambia una vez.
                const porDefecto =
                  listaDeUnidades.find((unidad) => unidad.codigo === 'UN') ?? listaDeUnidades[0];
                setUnidadDeStockId(porDefecto?.id ?? '');
                setAbierto(true);
              }}
            >
              {t('catalogo.nuevo')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="max-w-sm flex-1">
            <Campo
              etiqueta={t('catalogo.buscar')}
              type="search"
              value={busqueda}
              onChange={(evento) => {
                setBusqueda(evento.target.value);
              }}
            />
          </div>
          <Selector
            etiqueta={t('rubros.filtrar')}
            value={rubroFiltrado}
            onChange={(evento) => {
              setRubroFiltrado(evento.target.value);
            }}
          >
            <option value="">{t('rubros.todos')}</option>
            {aplanarElArbol(rubros.data ?? []).map((rubro) => (
              <option key={rubro.id} value={rubro.id}>
                {etiquetaSangrada(rubro.nombre, rubro.profundidad)}
              </option>
            ))}
          </Selector>
        </div>

        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio descripcion={t('catalogo.vacio')} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('catalogo.nuevo')}
        onCerrar={() => {
          setAbierto(false);
          setErrorDelAlta(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={codigo.trim() === '' || nombre.trim() === '' || unidadEfectivaId === ''}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Campo
              etiqueta={t('catalogo.codigo')}
              obligatorio
              value={codigo}
              maxLength={40}
              ayuda={t('catalogo.ayudaCodigo')}
              onChange={(evento) => {
                setCodigo(evento.target.value.toUpperCase());
              }}
            />
            <Campo
              etiqueta={t('catalogo.nombre')}
              obligatorio
              value={nombre}
              maxLength={200}
              onChange={(evento) => {
                setNombre(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('catalogo.marca')}
              value={marca}
              maxLength={80}
              onChange={(evento) => {
                setMarca(evento.target.value);
              }}
            />
            <Selector
              etiqueta={t('catalogo.tipo')}
              value={tipo}
              onChange={(evento) => {
                const elegido = evento.target.value === 'servicio' ? 'servicio' : 'producto';
                setTipo(elegido);
                // Un servicio no tiene existencia que descontar, y dejar el interruptor
                // encendido invitaría a esperar un stock que nunca va a moverse.
                if (elegido === 'servicio') setControlaExistencia(false);
              }}
            >
              <option value="producto">{t('catalogo.producto')}</option>
              <option value="servicio">{t('catalogo.servicio')}</option>
            </Selector>
          </div>

          <Selector
            etiqueta={t('catalogo.unidadDeStock')}
            obligatorio
            value={unidadEfectivaId}
            ayuda={t('catalogo.ayudaUnidad')}
            onChange={(evento) => {
              setUnidadDeStockId(evento.target.value);
            }}
          >
            {listaDeUnidades.map((unidad) => (
              <option key={unidad.id} value={unidad.id}>
                {etiquetaDeUnidad(unidad)}
              </option>
            ))}
          </Selector>

          {/* Los decimales se dicen acá y no cuando la venta los rechace. */}
          {unidadElegida === undefined ? null : (
            <p className="text-xs text-texto-tenue">
              {unidadElegida.decimales === 0
                ? t('catalogo.sinDecimales')
                : t('catalogo.conDecimales', { cantidad: unidadElegida.decimales })}
            </p>
          )}

          <Interruptor
            etiqueta={t('catalogo.controlaExistencia')}
            ayuda={t('catalogo.ayudaControlaExistencia')}
            checked={controlaExistencia}
            disabled={tipo === 'servicio'}
            onChange={(evento) => {
              setControlaExistencia(evento.target.checked);
            }}
          />

          <Selector
            etiqueta={t('catalogo.trazabilidad')}
            value={trazabilidad}
            ayuda={t('catalogo.ayudaTrazabilidad')}
            onChange={(evento) => {
              const elegida = TRAZABILIDADES.find((valor) => valor === evento.target.value);
              setTrazabilidad(elegida ?? 'ninguno');
            }}
          >
            {TRAZABILIDADES.map((valor) => (
              <option key={valor} value={valor}>
                {t(claveDeTrazabilidad(valor))}
              </option>
            ))}
          </Selector>

          <Interruptor
            etiqueta={t('catalogo.esInsumo')}
            ayuda={t('catalogo.ayudaEsInsumo')}
            checked={esInsumo}
            onChange={(evento) => {
              setEsInsumo(evento.target.checked);
            }}
          />

          <div className="grid grid-cols-2 gap-3">
            <Campo
              etiqueta={t('catalogo.talles')}
              value={talles}
              onChange={(evento) => {
                setTalles(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('catalogo.colores')}
              value={colores}
              onChange={(evento) => {
                setColores(evento.target.value);
              }}
            />
          </div>
          <p className="text-xs text-texto-tenue">{t('catalogo.ayudaCombinaciones')}</p>

          <ElegirRubros
            rubros={rubros.data ?? []}
            elegidos={rubrosElegidos}
            onCambiar={setRubrosElegidos}
          />

          <CamposDeLaFicha
            entidad="articulo"
            valores={camposDelAlta}
            onCambiar={(codigo, valor) => {
              setCamposDelAlta((previos) => ({ ...previos, [codigo]: valor }));
            }}
          />

          {aCrear.length > 1 ? (
            <p className="text-xs text-acento" aria-live="polite">
              {t('catalogo.combinacionesQueSeCrean', { count: aCrear.length })}
            </p>
          ) : null}

          {/* Con más de una variante los códigos de barra se cargan uno por uno desde la
              ficha: pegarlos todos acá no dice cuál es de cuál, y el servidor lo rechaza. */}
          {aCrear.length === 1 ? (
            <Campo
              etiqueta={t('catalogo.codigosDeBarra')}
              value={codigosDeBarra}
              ayuda={t('catalogo.ayudaCodigosDeBarra')}
              onChange={(evento) => {
                setCodigosDeBarra(evento.target.value);
              }}
            />
          ) : null}

          {errorDelAlta === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {errorDelAlta}
            </p>
          )}
        </div>
      </Dialogo>

      {/*
        Los códigos de barra, por variante.

        **El campo arranca con el foco puesto**: quien abre esto viene con el lector en la mano, y
        un lector escribe donde esté el cursor y manda un Enter al final. Si hay que hacer clic
        primero, el primer código se pierde en el aire.
      */}
      <Dialogo
        abierto={codigosDe !== null}
        titulo={t('catalogo.codigosDeBarra')}
        descripcion={codigosDe?.nombre ?? ''}
        onCerrar={() => {
          setCodigosDe(null);
        }}
      >
        <div className="flex flex-col gap-3">
          {codigos.isPending ? <Cargando /> : null}
          {codigos.isSuccess ? (
            <ul className="flex flex-col divide-y divide-borde-tenue">
              {codigos.data.map((fila) => (
                <li
                  key={`${fila.varianteId}-${fila.codigo ?? 'sin'}`}
                  className="flex items-center gap-3 py-2 text-cuerpo"
                >
                  <span className="min-w-0 flex-1">
                    {/* La variante única no se nombra: quien vende tornillos no tiene por qué
                        aprender esa palabra. */}
                    {fila.esUnica ? null : (
                      <span className="block text-apoyo text-texto-tenue">
                        {[fila.talle, fila.color].filter((parte) => parte !== null).join(' · ')}
                      </span>
                    )}
                    {fila.codigo === null ? (
                      <span className="text-texto-tenue">{t('catalogo.sinCodigoDeBarra')}</span>
                    ) : (
                      <span className="numero">{fila.codigo}</span>
                    )}
                  </span>
                  {fila.codigo === null ? null : (
                    <Boton
                      tono="sutil"
                      cargando={sacarCodigo.isPending}
                      onClick={() => {
                        sacarCodigo.mutate(fila);
                      }}
                    >
                      {t('catalogo.sacarCodigo')}
                    </Boton>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          <form
            className="flex items-end gap-2"
            onSubmit={(evento) => {
              // El lector manda un Enter al final: sin el formulario, ese Enter no hace nada y hay
              // que ir con el mouse hasta el botón por cada caja.
              evento.preventDefault();
              if (codigoNuevo.trim() !== '' && varianteEfectiva !== '') agregarCodigo.mutate();
            }}
          >
            <Selector
              etiqueta={t('catalogo.paraLaVariante')}
              value={varianteEfectiva}
              onChange={(evento) => {
                setVarianteElegida(evento.target.value);
              }}
            >
              {[
                ...new Map((codigos.data ?? []).map((fila) => [fila.varianteId, fila])).values(),
              ].map((fila) => (
                <option key={fila.varianteId} value={fila.varianteId}>
                  {fila.esUnica
                    ? fila.varianteCodigo
                    : [fila.talle, fila.color].filter((parte) => parte !== null).join(' · ')}
                </option>
              ))}
            </Selector>
            <Campo
              etiqueta={t('catalogo.codigoDeBarra')}
              value={codigoNuevo}
              maxLength={64}
              // El foco se pone con una referencia y no con `autoFocus`, y no es para esquivar el
              // lint: la regla existe porque enfocar algo al cargar una página desorienta a quien
              // usa lector de pantalla. Adentro de un diálogo que la persona **acaba de abrir**,
              // mover el foco al primer campo es lo que corresponde hacer.
              ref={(campo: HTMLInputElement | null) => {
                campo?.focus();
              }}
              onChange={(evento) => {
                setCodigoNuevo(evento.target.value);
              }}
            />
            <Boton tono="principal" type="submit" cargando={agregarCodigo.isPending}>
              {t('acciones.agregar')}
            </Boton>
          </form>

          {errorDeCodigos === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {errorDeCodigos}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
